import { faEye, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactNode } from 'react';

import { Alert } from '@design-system/components/alert/alert';
import { Button } from '@design-system/components/button/button';
import { IconButton } from '@design-system/components/button/iconButton';
import { Drawer } from '@design-system/components/modal/sideModal';
import { Switch } from '@design-system/components/switch/switch';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { Typography } from '@design-system/components/typography/typography';

import { copyFor, isKnownAutomationType } from '../../automation-copy';
import {
  AutomationDetailsViewModel,
  Timeline,
  useAutomationDetailsViewModel,
} from '../../view-model/AutomationDetails.viewModel';
import * as styles from '../Automations.view.css';
import { WIDE_DRAWER_SX } from './AutomationInsightsDrawer.view';
import { AutomationSendTestView } from './AutomationSendTest.view';

type AutomationDetailsModalProps = {
  creatorId: number;
  /** null keeps the panel closed. */
  automationType: string | null;
  onClose: () => void;
  /** Per-type "What we need from you" form (GRW-742/743/744). Receives the
   *  fetched view model so it can read and stage the draft. */
  renderConfigForm?: (vm: AutomationDetailsViewModel) => ReactNode;
};

export const AutomationDetailsModal = ({
  creatorId,
  automationType,
  onClose,
  renderConfigForm,
}: AutomationDetailsModalProps) => (
  <Drawer
    isOpen={automationType !== null}
    onClose={onClose}
    drawerSx={WIDE_DRAWER_SX}
  >
    <div className={styles.drawer} data-testid="details-panel">
      {automationType !== null ? (
        <DetailsBody
          key={automationType}
          creatorId={creatorId}
          automationType={automationType}
          onClose={onClose}
          renderConfigForm={renderConfigForm}
        />
      ) : null}
    </div>
  </Drawer>
);

const DetailsBody = ({
  creatorId,
  automationType,
  onClose,
  renderConfigForm,
}: Omit<AutomationDetailsModalProps, 'automationType'> & {
  automationType: string;
}) => {
  const vm = useAutomationDetailsViewModel(creatorId, automationType);

  if (vm.type === 'not-fetched' || vm.type === 'fetching') {
    return (
      <div
        className={styles.detailsLoading}
        aria-busy
        data-testid="details-loading"
      />
    );
  }
  if (vm.type === 'fetch-error') {
    return (
      <div className={styles.detailsLoading}>
        <Alert
          variant="error"
          details={vm.value.message}
          action={
            vm.value.retry
              ? { label: 'Try again', onClick: () => void vm.value.retry?.() }
              : { label: 'Close', onClick: onClose }
          }
        />
      </div>
    );
  }

  const details = vm.value;
  const form = renderConfigForm?.(details);
  const showsNeeds = details.configFields.length > 0 || details.readinessReason;
  const enableBlocked =
    !details.draft.enabled && details.enableBlockedHint !== null;
  const control = (
    <div>
      <Switch
        checked={details.draft.enabled}
        onChange={details.setEnabled}
        disabled={details.saving || enableBlocked}
        showLabel
        ariaLabel={`${details.displayName}: ${
          details.draft.enabled ? 'on' : 'off'
        }`}
        testId="details-switch"
      />
    </div>
  );

  return (
    <div className={styles.details} data-testid={`details-${automationType}`}>
      <header className={styles.detailsHeader}>
        <IconButton
          icon={faTimes}
          variant="text"
          color="neutral"
          aria-label="Close"
          onClick={onClose}
        />
        <div className={styles.detailsHeading}>
          <Typography as="h2" variant="headline" size="medium">
            {details.displayName}
          </Typography>
          <span className={styles.detailsTrigger}>{details.triggerLine}</span>
        </div>
        <div className={styles.detailsActions}>
          {enableBlocked ? (
            <Tooltip
              variant="dark"
              title={details.enableBlockedHint}
              placement="top"
            >
              {control}
            </Tooltip>
          ) : (
            control
          )}
          <Button
            color="neutral"
            size="medium"
            disabled={details.primaryAction === 'save' && !details.canSave}
            loading={details.saving}
            onClick={async () => {
              if (details.primaryAction === 'done') return onClose();
              if (await details.save()) onClose();
            }}
          >
            {details.primaryAction === 'done' ? 'Done' : 'Save'}
          </Button>
        </div>
      </header>

      <div className={styles.detailsBody}>
        <p className={styles.detailsDescription}>{details.description}</p>

        {showsNeeds ? (
          <section
            className={styles.detailsSection}
            aria-label="What we need from you"
          >
            <div className={styles.detailsSectionHead}>
              <span className={styles.detailsSectionTitle}>
                What we need from you
              </span>
              <span className={styles.detailsSectionHint}>
                {details.readinessReason === null
                  ? 'All set. Change any of it whenever you like.'
                  : details.setupHint}
              </span>
            </div>
            {form}
          </section>
        ) : null}

        {details.error ? (
          <p className={styles.rowError} role="alert">
            {details.error}
          </p>
        ) : null}

        <section className={styles.detailsSection} aria-label="What goes out">
          <div className={styles.detailsSectionHead}>
            <span className={styles.detailsSectionTitle}>What goes out</span>
            <span className={styles.detailsSectionAside}>
              <span className={styles.detailsCadence}>{details.cadence}</span>
              <a
                className={styles.previewPill}
                href={copyFor(automationType).helpArticleUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="Preview these messages in the Help Center (opens in a new tab)"
              >
                <FontAwesomeIcon icon={faEye} fontSize={14} />
                Preview
              </a>
            </span>
          </div>
          <span className={styles.detailsSectionHint}>
            Written and managed by Playbook, personalized with your name,
            branding and program details.
          </span>
          <TimelineView timeline={details.timeline} />
        </section>

        {details.exitLine ? (
          <p className={styles.detailsFooter}>{details.exitLine}</p>
        ) : null}

        {details.adminTools && isKnownAutomationType(automationType) ? (
          <section
            className={styles.adminToolsInModal}
            aria-label="Send test (admin)"
          >
            <span className={styles.detailsSectionTitle}>
              Send test (admin)
            </span>
            <AutomationSendTestView
              creatorId={creatorId}
              automationType={automationType}
            />
          </section>
        ) : null}
      </div>
    </div>
  );
};

const TimelineView = ({ timeline }: { timeline: Timeline }) => {
  if (timeline.type === 'loading') {
    return (
      <div
        className={styles.skeleton}
        aria-busy
        data-testid="timeline-loading"
      />
    );
  }
  if (timeline.type === 'error') {
    return (
      <Alert
        variant="warning"
        details={timeline.message}
        action={{ label: 'Try again', onClick: () => void timeline.retry() }}
      />
    );
  }
  return (
    <ol className={styles.timeline}>
      {timeline.rows.map((row) => (
        <li className={styles.timelineRow} key={row.key}>
          <span className={styles.timelineTiming}>{row.timing}</span>
          <span className={styles.timelineCopy}>
            <span className={styles.timelineSubject}>{row.subject}</span>
            <span className={styles.timelinePreview}>{row.preview}</span>
          </span>
          <span
            className={
              row.channel === 'push'
                ? styles.channelChipPush
                : styles.channelChipEmail
            }
          >
            {row.channelLabel}
          </span>
        </li>
      ))}
    </ol>
  );
};
